/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import { downloadFile } from '../_helpers/dashboardHelper';
import { exportData } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import MultiSelectDropdown from '../controls/MultiSelectDropDown';
import CustomDropdown from '../controls/CustomDropdown';
import CustomDatePicker from '../controls/CustomDatePicker';
import { GA_CATEGORIES, sendCustomEventToGa } from '../_helpers/ga';
import usePlan from '../hooks/usePlan';

/** Days of history allowed per plan. Null = unlimited, 0 = no download. */
const PLAN_DOWNLOAD_DAYS: Record<string, number | null> = {
  Free: 0,
  Basic: 7,
  Advanced: 30,
  Professional: 90,
  Enterprise: null
};

const PLAN_DOWNLOAD_LABEL: Record<string, string> = {
  Free: 'No data download',
  Basic: '7 days of data storage and download',
  Advanced: '1 month of data storage and download',
  Professional: '3 months of data storage and download',
  Enterprise: 'Unlimited data storage'
};

const TIME_RESOLUTION_OPTIONS = [
  { label: 'Raw', value: '3' },
  { label: '10sec', value: '10' },
  { label: '30sec', value: '30' },
  { label: '1min', value: '60' },
  { label: '5min', value: '300' },
  { label: '30min', value: '1800' },
  { label: '1hr', value: '3600' },
  { label: '1day', value: '86400' }
];

type DownloadModalProps = {
  open: boolean;
  setOpen: (val: boolean) => void;
  onDownload: () => void;
};

type FORMDATA = {
  devices: {
    label: string;
    value: string;
  }[];
  parameters: {
    label: string;
    value: string;
  }[];
  startTime: Date;
  endTime: Date;
  interval: string;
  groups: {
    label: string;
    value: string;
  }[];
};

/**
 * Modal component for Download confirmation.
 * @param props - Properties of the DownloadModal component.
 * @returns - React Element.
 */
export default function DownloadModal(props: DownloadModalProps): ReactElement {
  const { open, setOpen } = props;
  const { devices: deviceList, parameters, groupedData: groups } = useData();
  const { planName } = usePlan();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const allowedDays = PLAN_DOWNLOAD_DAYS[planName] ?? null;
  const canDownload = allowedDays !== 0;

  const minStartDate = useMemo(() => {
    if (allowedDays === null) return null; // unlimited
    const d = new Date();
    d.setDate(d.getDate() - allowedDays);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [allowedDays]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [intervals, setIntervals] = useState<
    { label: string; value: string; disabled?: boolean }[]
  >([]);
  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: {
      startTime: new Date(),
      endTime: new Date()
    }
  });

  const { handleSubmit, control, watch, setValue } = methods;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const parameterList = parameters.map((item) => ({
    label: item.displayName,
    value: item.parameterName
  }));

  const onDownload = async (formData: FORMDATA): Promise<void> => {
    if (!canDownload) return;

    if (allowedDays !== null) {
      const diffMs = new Date(formData.endTime).getTime() - new Date(formData.startTime).getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays > allowedDays) {
        toast.error(`Your ${planName} plan allows up to ${allowedDays} day${allowedDays !== 1 ? 's' : ''} of data. Please shorten the date range.`);
        return;
      }
    }

    try {
      let devices = [];
      let params = [];
      const hasAllDevices = formData.devices?.find((item) => item.value === '0');
      const hasAllParameters = formData.parameters?.find((item) => item.value === '0');

      if (hasAllDevices) {
        devices = deviceList.map((item) => item.serialNumber);
      } else {
        devices = formData.devices?.map((item) => parseInt(item.value, 10));
      }

      if (hasAllParameters) {
        params = parameterList.map((item) => item.value);
      } else {
        params = formData.parameters?.map((item) => item.value);
      }

      const data = {
        deviceIds: devices || [],
        startDate: new Date(formData.startTime).getTime(),
        endDate: new Date(formData.endTime).getTime(),
        parameters: params || [],
        interval: `${formData.interval}s`
      } as any;

      setLoading(true);
      sendCustomEventToGa(GA_CATEGORIES.download, 'download-started');
      sendCustomEventToGa(GA_CATEGORIES.download, 'download-interval', {
        value: parseInt(formData.interval, 10),
        label: 'Interval'
      });

      const response = await exportData(data);

      // Check if response data is JSON and contains viaEmail: true
      if (typeof response?.data === 'object' && response?.data?.viaEmail === true) {
        toast.success('Data will be sent via email shortly');
        setOpen(false);
        return;
      }

      if (response?.data.length === 0) {
        toast.error("Data isn't available for selected time period and parameter");
        return;
      }
      const start = new Date(formData.startTime);
      const pad = (n: number): string => n.toString().padStart(2, '0');
      const fileStamp = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}_${pad(start.getHours())}-${pad(start.getMinutes())}`;
      downloadFile(response.data, `Dashboard_${fileStamp}.csv`, 'text/csv');
      toast.success('File downloaded successfully');
      setOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error('File download failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedGroups = watch('groups');
  const selectedDevices = watch('devices');
  const selectedParameters = watch('parameters');
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateIntervalOptions = (): { label: string; value: string; disabled?: boolean }[] => {
    if (!startTime || !endTime)
      return TIME_RESOLUTION_OPTIONS.map((option) => ({ ...option, disabled: true }));

    const totalDuration = new Date(endTime).getTime() - new Date(startTime).getTime();
    return TIME_RESOLUTION_OPTIONS.filter(
      (option) => totalDuration / (parseInt(option.value, 10) * 1000) <= 15000
    );
  };

  useEffect(() => {
    setIntervals(calculateIntervalOptions());
  }, [startTime, endTime]);

  useEffect(() => {
    if (open) {
      sendCustomEventToGa(GA_CATEGORIES.download, 'download-modal-opened', {
        label: 'download-modal-opened'
      });
    }
  }, [open]);

  useEffect(() => {
    if (!selectedGroups) return;

    const devicesList: number[] = [];
    const selectedGroupsWithData =
      selectedGroups.map((group) => groups.find((item) => item.groupId === Number(group.value))) ||
      [];

    selectedGroupsWithData.forEach((group) => {
      if (group?.sensorDetailsList) {
        devicesList.push(...group.sensorDetailsList.map((item) => Number(item.serial_number)));
      }
    });
    const updatedDevices = devicesList.map((device) => {
      const deviceData = deviceList.find((item) => item.serialNumber === device);
      return {
        label: deviceData?.deviceName || deviceData?.serialNumber.toString() || '',
        value: deviceData?.serialNumber.toString() || ''
      };
    });

    setValue('devices', updatedDevices);
  }, [selectedGroups]);

  const parameterOptionList = selectedParameters?.find((item) => item.value === '0')
    ? [{ label: 'All', value: '0' }]
    : [
        { label: 'All', value: '0' },
        ...parameterList.filter((item) => item.value !== 'aqi' && item.value !== 'ci')
      ];

  const deviceOptionList = selectedDevices?.find((item) => item.value === '0')
    ? [{ label: 'All', value: '0' }]
    : [
        { label: 'All', value: '0' },
        ...deviceList.map((device) => ({
          label: device.deviceName || device.serialNumber.toString(),
          value: device.serialNumber.toString()
        }))
      ];
  if (!open) return <div />;

  return (
    <div className="fixed flex inset-0 bg-background-600/90 z-[1000]">
      <div className="flex min-h-full flex-1 min-w-full items-center justify-center p-4 text-center sm:p-0">
        <div className="relative transform flex flex-1 rounded-lg max-w-4xl w-full bg-background-900 text-center shadow-xl transition-all p-2">
          <div className="px-4 pb-4 pt-5 w-full">
            <div className="sm:flex sm:items-center">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-white text-sm font-medium">Download Data</h3>
                <div className={`mt-3 flex items-center gap-2 rounded border px-3 py-2 text-xs ${canDownload ? 'border-primary-500/30 bg-primary-500/10 text-primary-300' : 'border-error-500/30 bg-error-500/10 text-error-300'}`}>
                  <span className="material-icons-outlined text-sm">
                    {canDownload ? 'info' : 'block'}
                  </span>
                  <span>
                    <span className="font-semibold">{planName} plan:</span>{' '}
                    {PLAN_DOWNLOAD_LABEL[planName] ?? 'Unlimited data storage'}
                    {!canDownload && (
                      <button
                        type="button"
                        className="ml-2 underline"
                        onClick={() => { setOpen(false); navigate('/pricing'); }}
                      >
                        Upgrade to download data
                      </button>
                    )}
                  </span>
                </div>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <FormProvider {...methods}>
                  <form noValidate action="post" onSubmit={handleSubmit(onDownload)}>
                    <MultiSelectDropdown
                      id="groups"
                      label="Groups"
                      control={control}
                      options={groups.map((group) => ({
                        label: group.groupName,
                        value: group.groupId.toString()
                      }))}
                    />
                    <MultiSelectDropdown
                      id="devices"
                      label="Devices"
                      validationObject={{ required: 'Please select at least one device' }}
                      control={control}
                      options={deviceOptionList}
                    />
                    <MultiSelectDropdown
                      id="parameters"
                      label="Parameters"
                      control={control}
                      options={parameterOptionList}
                    />
                    {/* <CustomDropdown
                      label="Parameter"
                      id="parameter"
                      options={parameterList}
                      validationObject={{ required: 'Please select a parameter' }}
                    /> */}
                    <div className="relative flex gap-3">
                      <CustomDatePicker
                        label="Start Time"
                        id="startTime"
                        validationObject={{ required: 'Please enter the start time' }}
                        control={control}
                        defaultValue={methods.getValues('startTime')}
                        minDate={minStartDate}
                        disabled={!canDownload}
                      />
                      <CustomDatePicker
                        label="End Time"
                        id="endTime"
                        validationObject={{ required: 'Please enter the end time' }}
                        control={control}
                        defaultValue={methods.getValues('endTime')}
                        disabled={!canDownload}
                      />
                    </div>
                    <CustomDropdown
                      label="Time Resolution"
                      id="interval"
                      options={intervals}
                      validationObject={{ required: 'Please select a time resolution' }}
                    />
                    <div className="bg-background-900 flex gap-2 justify-between mt-8">
                      <div className="text-sm text-white/50">Data format: CSV*</div>
                      <div className="flex gap-2">
                        <Button
                          theme="secondary-color"
                          size="sm"
                          name="Cancel"
                          onClick={() => setOpen(false)}
                        />
                        <Button
                          theme="primary"
                          loading={loading}
                          size="sm"
                          name="Download"
                          buttonType="submit"
                          disabled={!canDownload}
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
